import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Grid, Card, Tooltip, useMediaQuery } from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import { DeleteIcon, FileCopyIcon, FilterIcon } from "@icarius-icons";
import { FiltersDialog } from "@icarius-common/filtersDialog";
import useFilterDialog from "@icarius-common/filtersDialog/components/useFilterDialog";
import DataContainer from "@icarius-common/abmComponents/dataContainer";
import { GetAppIcon } from "@icarius-icons/index";
import { getAppColor } from "@icarius-app/selectors";
import { openDialogAction } from "@icarius-common/dialog/actions";
import { deletePollSettingsAction, getPollSettingsAction } from "../../actions";
import Form from "./form";

const Item = (props) => {
    const {
        data,
        pollNames,
        visualizationTypeList,
        modes,
        processList,
        pollTypes,
        closeNewPoll,
        openDuplicateDialog,
        exportPoll,
        isLoading,
        getIsActive,
        onaTypeList,
        positionList,
        employeeList,
    } = props;

    const dispatch = useDispatch();
    const color = useSelector(getAppColor);
    const hideWhitespace = useMediaQuery("(max-width: 959px)");

    const isCreate = !Boolean(data);

    const [isOpen, setIsOpen] = useState(isCreate); //si viene sin data, que este abierto por defecto
    const { filtersDialogIsOpen, setFiltersDialogIsOpen } = useFilterDialog();

    const handleDeletePoll = () => {
        if (isCreate) {
            closeNewPoll();
        } else {
            dispatch(deletePollSettingsAction(data.code, data.name));
        }
    };

    const roleText = useMemo(() => {
        if (isCreate || !data.visualizationType) return "";

        return visualizationTypeList.find((role) => role.key === data.visualizationType).value;
    }, [data, visualizationTypeList, isCreate]);

    const pollTypeText = useMemo(() => {
        if (isCreate || !data.type) return "";

        return `${pollTypes.find((type) => type.key === data.type).value}${data.isAnonymous === "Y" ? "/Anónima" : ""}${
            data.useForOnboarding === "Y" ? "/De Onboarding" : ""
        }`;
    }, [data, pollTypes, isCreate]);

    const handleClosePoll = () => {
        Boolean(data) && setIsOpen((prev) => !prev);
    };

    const getHasFilters = () => {
        return Object.keys(data.filterData).some((filterName) => !data.filterData[filterName].includes("-"));
    };

    const handleOpenDeleteDialog = () => {
        const message = (() => {
            if (!data) return getLocalizedString("confirmDeletePoll");
            if (data.inUse) return getLocalizedString("pollDeleteIsInUse", { name: data.name });
            return getLocalizedString("pollDeleteQuestion", { name: data.name });
        })();

        dispatch(
            openDialogAction({
                title: getLocalizedString("pollDeleteDialogTitle"),
                msg: message,
                onConfirm: handleDeletePoll,
                acceptText: getLocalizedString("delete"),
            })
        );
    };

    return (
        <>
            <Card
                style={{
                    width: "100%",
                    borderRadius: 10,
                    background: isOpen || isCreate ? color : "",
                    color: (isOpen || isCreate) && "white",
                }}
            >
                <Grid container item xs={12} direction="row" style={{ padding: "10px 20px" }}>
                    <Grid
                        container
                        item
                        xs={10}
                        style={{ cursor: Boolean(data) ? "pointer" : "" }}
                        onClick={handleClosePoll}
                    >
                        {isCreate ? (
                            <DataContainer sm={12} md={12} lg={12} text={getLocalizedString("pollCreation")} />
                        ) : (
                            <>
                                <DataContainer lg={4} md={4} sm={12} text={data.name} />
                                <DataContainer
                                    lg={4}
                                    md={4}
                                    sm={6}
                                    text={getLocalizedString(getIsActive(data) ? "active" : "inactive")}
                                />
                                <DataContainer lg={4} md={4} sm={6} text={`${data.startDate} - ${data.endDate}`} />
                                {!hideWhitespace && <DataContainer lg={4} md={4} sm={6} text={""} />}
                                <DataContainer lg={4} md={4} sm={6} text={pollTypeText} />
                                <DataContainer lg={4} md={4} sm={6} text={roleText} />
                            </>
                        )}
                    </Grid>
                    <Grid container direction="row" item xs={2} justify={isCreate ? "flex-end" : "space-around"}>
                        {!isCreate && (
                            <Tooltip title={getLocalizedString("exportSurvey")}>
                                <IconButton size={"small"} disableRipple onClick={() => exportPoll(data)}>
                                    <GetAppIcon style={{ fill: "var(--mainText)" }} fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!isCreate && (
                            <Tooltip title={getLocalizedString("pollDuplication")}>
                                <IconButton size={"small"} disableRipple onClick={() => openDuplicateDialog(data)}>
                                    <FileCopyIcon style={{ fill: "var(--mainText)" }} fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {!isCreate && data.useForOnboarding !== "Y" && data.type !== "O" && (
                            <Tooltip title={getLocalizedString("filter")}>
                                <IconButton size={"small"} disableRipple onClick={() => setFiltersDialogIsOpen(true)}>
                                    <FilterIcon
                                        style={{ fill: getHasFilters() ? color : "var(--mainText)", height: "15px" }}
                                        fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={getLocalizedString("delete")}>
                            <IconButton size={"small"} disableRipple onClick={handleOpenDeleteDialog}>
                                <DeleteIcon style={{ fill: "var(--mainText)" }} fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Card>
            {isOpen && (
                <Form
                    data={data}
                    visualizationTypeList={visualizationTypeList}
                    modes={modes}
                    processList={processList}
                    pollTypes={pollTypes}
                    pollNames={pollNames}
                    onaTypeList={onaTypeList}
                    positionList={positionList}
                    employeeList={employeeList}
                    closePoll={isCreate ? closeNewPoll : handleClosePoll}
                />
            )}
            {!isCreate && filtersDialogIsOpen && !isLoading && (
                <FiltersDialog
                    open={filtersDialogIsOpen}
                    type={"ENC"}
                    code={data.code}
                    data={data.filterData}
                    isDisabled={data?.inUse}
                    handleFetchUpdatedData={() => dispatch(getPollSettingsAction())}
                    handleClose={() => setFiltersDialogIsOpen(false)}
                />
            )}
        </>
    );
};

export default Item;
